import { Reducer, useEffect, useReducer } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Button,
} from '@mui/material';

import { useUserTerms } from './hooks/useUserTerms';
import { SelectPayment } from './components/SelectPayment';
import { EnterEmail } from './components/EnterInformation';
import { Payment } from './components/Payment';
import { ArrowBack } from '@mui/icons-material';
import { SessionInformation } from '../../hooks/useStepHandler';
import { Site } from '../../../generated/graphql';
import { StepType } from '../../types/step';
import { t } from 'i18next';

interface IProps {
  details: SessionInformation;
  open: boolean;
  site: Site;
  onClose: () => void;
  amount: number;
  processPaymentCallback: (
    site: Site | null,
    sessionInformation: SessionInformation,
  ) => Promise<void>;
  setVrm: (vrm: string) => void;
  setStep: (step: StepType) => void;
  paymentLinkLoading: boolean;
  showVrm?: boolean;
}

enum PaymentStep {
  SELECT_PAYMENT = 'selectPayment',
  ENTER_INFORMATION = 'enterEmail',
  PAYMENT = 'payment',
}

interface IState {
  currentStep: PaymentStep;
  paymentMethod: string;
  details: SessionInformation;
}

enum PaymentAction {
  GO_BACK = 'GO_BACK',
  GO_FORWARD = 'GO_FORWARD',
  SELECT_PAYMENT = 'SELECT_PAYMENT',
  SET_PAYMENT_METHOD = 'SET_PAYMENT_METHOD',
  CONTINUE_TO_PAYMENT = 'CONTINUE_TO_PAYMENT',
}

const navigationBar: Record<
  PaymentStep,
  { title: string; showBackButton: boolean }
> = {
  [PaymentStep.SELECT_PAYMENT]: {
    title: 'selectPaymentMethod',
    showBackButton: false,
  },
  [PaymentStep.ENTER_INFORMATION]: {
    title: 'enterInformation',
    showBackButton: true,
  },
  [PaymentStep.PAYMENT]: {
    title: 'confirmPayment',
    showBackButton: true,
  },
};

const reducer = (state: any, action: any) => {
  const { currentStep } = state;
  switch (action.type) {
    case PaymentAction.SELECT_PAYMENT:
      return { ...state, currentStep: action.payload };
    case PaymentAction.SET_PAYMENT_METHOD:
      return { ...state, paymentMethod: action.payload };
    case PaymentAction.GO_BACK:
      return {
        ...state,
        currentStep:
          currentStep === PaymentStep.ENTER_INFORMATION
            ? PaymentStep.SELECT_PAYMENT
            : PaymentStep.ENTER_INFORMATION,
      };
    case PaymentAction.GO_FORWARD:
      const payload = action.payload || state;
      return {
        ...state,
        ...(currentStep === PaymentStep.ENTER_INFORMATION && {
          details: {
            ...state.details,
            ...payload,
            ...(payload.phone && { receiveSms: true }),
            ...(payload.email && { receiveEmails: true }),
          },
        }),
        currentStep:
          currentStep === PaymentStep.SELECT_PAYMENT
            ? PaymentStep.ENTER_INFORMATION
            : PaymentStep.PAYMENT,
      };
    case PaymentAction.CONTINUE_TO_PAYMENT:
      return {
        ...state,
        email: action.payload,
        currentStep: PaymentStep.PAYMENT,
      };
  }
};

export const PaymentFlow = ({
  details,
  open,
  onClose,
  site,
  amount,
  processPaymentCallback,
  setVrm,
  setStep,
  paymentLinkLoading,
  showVrm = true,
}: IProps) => {
  const { isUserTermsChecked, handleUserTermsChange } = useUserTerms();
  const [currentStep, dispatch] = useReducer<Reducer<IState, any>>(reducer, {
    currentStep: PaymentStep.SELECT_PAYMENT,
    paymentMethod: '',
    details,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handlePaymentChange = (paymentMethod: string) => {
    dispatch({
      type: PaymentAction.SET_PAYMENT_METHOD,
      payload: paymentMethod,
    });
  };

  const handleGoBack = () => {
    dispatch({
      type: PaymentAction.GO_BACK,
    });
  };

  useEffect(() => {
    if (showVrm) {
      setVrm(currentStep.details.vrm);
      setStep('newVrm');
    }
  }, [currentStep.details.vrm, setVrm, setStep, showVrm]);

  const handleNext = (details?: {
    email: string;
    vrm: string;
    phone: string;
    receiveSms: boolean;
    receiveEmails: boolean;
  }) => {
    dispatch({
      type: PaymentAction.GO_FORWARD,
      payload: details,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      keepMounted
      PaperProps={{
        sx: {
          position: isMobile ? 'fixed' : 'absolute',
          top: isMobile ? '50%' : '20%',
          left: '50%',
          transform: isMobile ? 'translate(-50%, -50%)' : 'translateX(-50%)',
          m: 0,
          borderRadius: isMobile
            ? theme.shape.borderRadius
            : theme.shape.borderRadius,
          maxHeight: isMobile ? '90vh' : '80vh',
          width: isMobile ? '95%' : '100%',
          padding: 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 16px 0px 16px',
        }}
      >
        {navigationBar[currentStep.currentStep].showBackButton && (
          <Button
            variant="text"
            color="primary"
            sx={{ mb: 2, p: 0 }}
            onClick={handleGoBack}
          >
            <ArrowBack />
          </Button>
        )}
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          {t(navigationBar[currentStep.currentStep].title)}
        </Typography>
      </Box>
      <DialogContent
        sx={{
          p: isMobile ? 2 : 3,
          overflow: 'visible',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            minHeight: '100px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '16px',
          }}
        >
          {currentStep.currentStep === PaymentStep.SELECT_PAYMENT && (
            <SelectPayment
              selectedPayment={currentStep.paymentMethod}
              handlePaymentChange={handlePaymentChange}
              isUserTermsChecked={isUserTermsChecked}
              handleUserTermsChange={handleUserTermsChange}
              handlePaymentNext={handleNext}
            />
          )}
          {currentStep.currentStep === PaymentStep.ENTER_INFORMATION && (
            <EnterEmail
              email={currentStep.details.email}
              handleNext={handleNext}
              vrm={currentStep.details.vrm}
              phone={currentStep.details.phone}
              receiveSms={currentStep.details.receiveSms}
              showVrm={showVrm}
            />
          )}

          {currentStep.currentStep === PaymentStep.PAYMENT && (
            <Payment
              sessionInformation={currentStep.details}
              email={currentStep.details.email}
              paymentMethod={currentStep.paymentMethod}
              site={site}
              amount={amount}
              processPaymentCallback={processPaymentCallback}
              paymentLinkLoading={paymentLinkLoading}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
